import React, { KeyboardEvent, useState } from 'react';

import { Box, Input, Stack } from '@chakra-ui/react';

import { Tag } from 'components/DataDisplay';

import { commonFieldStyles } from '../../Form/styles';
import {
  OutlinedFormFieldComponentProps,
  OutlinedFormMultiTextField,
} from '../types';

import { FieldContainer } from './FieldContainer';

export const MultiTextField = ({
  field,
  styles,
}: OutlinedFormFieldComponentProps<OutlinedFormMultiTextField>) => {
  const [inputValue, setInputValue] = useState('');
  const values = field.value ?? [];
  const separatorKeys = field.separatorKeys ?? ['Enter'];

  // Commit only complete, unique values; transient input stays local to this field.
  const addValue = () => {
    const value = inputValue.trim();
    setInputValue('');

    if (!value || values.includes(value)) return;
    field.onChange?.([...values, value]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!separatorKeys.includes(event.key)) return;

    event.preventDefault();
    addValue();
  };

  return (
    <Stack spacing={3}>
      <FieldContainer
        fieldStyles={field.styles}
        isDisabled={field.isDisabled}
        label={field.label}
        styles={styles}
      >
        <Input
          autoComplete="off"
          mt={1}
          h={7}
          p={0}
          border="none"
          borderRadius={0}
          fontSize={commonFieldStyles.fontSize}
          lineHeight="short"
          _hover={{ borderColor: 'transparent' }}
          _active={{ borderColor: 'transparent' }}
          _focus={{ borderColor: 'transparent', boxShadow: 'none' }}
          _focusVisible={{ boxShadow: 'none' }}
          {...styles?.value}
          {...field.styles?.value}
          {...field.inputProps}
          isDisabled={field.isDisabled}
          name={field.name}
          onChange={event => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={field.placeholder}
          value={inputValue}
        />
      </FieldContainer>
      {/* Keep committed values outside the input outline so its height stays stable. */}
      {!!values.length && (
        <Box display="flex" flexWrap="wrap" gap={2}>
          {values.map(value => (
            <Tag
              key={value}
              label={value}
              closeButton
              onClose={() =>
                field.onChange?.(values.filter(item => item !== value))
              }
              styles={{
                tag: {
                  m: 0,
                  px: 2,
                  py: 0,
                  bg: 'hover',
                  border: '1px solid',
                  borderColor: 'primary',
                  fontSize: 'sm',
                },
                closeButton: { bg: 'transparent' },
              }}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};
